/**
 * Rolled Steel Equal Angle Sections - Dimensions and Properties
 * Data source: "ROLLED STEEL EQUAL ANGLES dimensions and properties.csv"
 * 
 * EQUAL ANGLE CROSS-SECTION GEOMETRY (L-Section):
 * 
 *        ← A (Leg length) →
 *        ┌─────────────────┐  ↑
 *        │  ← t →          │  │
 *        ├──────────────────┤  │
 *        │                     │ B (Leg length)
 *        │  ┌────             │ (A = B for equal angles)
 *        │  │ r1              │
 *        │  │                 │
 *        └──┘                 ↓
 *        
 *        Centroid ↓
 *        (Cxx, Cyy from back edges)
 *        
 * Key Dimensions:
 * - A × B: Size of the two legs (A = B for equal angles)
 * - t: Thickness of the legs
 * - r1: Radius at Root (internal fillet curve)
 * - r2: Radius at Toe (external edge curve)
 * - Cxx = Cyy: Centre of Gravity (distance from back edges)
 * 
 * Principal Axes:
 * - X-X and Y-Y: Parallel to the legs
 * - U-U and V-V: Principal axes (rotated 45° from X-X and Y-Y)
 */

export interface RolledSteelEqualAngleData {
  designation: string;           // e.g., "ISA 2020"
  size: string;                   // e.g., "20 x 20" (A × B in mm)
  A: number;                      // Leg length A (mm) - parsed from size
  B: number;                      // Leg length B (mm) - parsed from size (A = B)
  t: number;                      // Thickness (mm)
  a: number;                      // Area (cm²)
  w: number;                      // Weight (kg/m)
  Cxx: number;                    // Centre of Gravity X-X (cm) (Cxx = Cyy for equal angles)
  Cyy: number;                    // Centre of Gravity Y-Y (cm) (same as Cxx)
  exx: number;                    // Distance to extreme fiber X-X (cm)
  eyy: number;                    // Distance to extreme fiber Y-Y (cm)
  Ixx: number;                    // Moment of Inertia about X-X (cm⁴)
  Iyy: number;                    // Moment of Inertia about Y-Y (cm⁴)
  Iuu: number;                    // Moment of Inertia about U-U (cm⁴)
  Ivv: number;                    // Moment of Inertia about V-V (cm⁴)
  Ixy: number;                    // Product of Inertia (cm⁴)
  rxx: number;                    // Radius of Gyration about X-X (cm)
  ryy: number;                    // Radius of Gyration about Y-Y (cm)
  ruu: number;                    // Radius of Gyration about U-U (cm)
  rvv: number;                    // Radius of Gyration about V-V (cm)
  Zxx: number;                    // Section Modulus about X-X (cm³)
  Zyy: number;                    // Section Modulus about Y-Y (cm³)
  r1: number;                     // Radius at Root (mm)
  r2: number;                     // Radius at Toe (mm)
}

/**
 * Complete dataset of Rolled Steel Equal Angle sections from CSV
 * All dimensions and properties from standard rolled steel angle specifications
 */
export const rolledSteelEqualAngleSections: RolledSteelEqualAngleData[] = [
  {
    designation: "ISA 2020",
    size: "20 x 20",
    A: 20,
    B: 20,
    t: 3,
    a: 1.12,
    w: 0.9,
    Cxx: 0.59,
    Cyy: 0.59,
    exx: 1.41,
    eyy: 1.41,
    Ixx: 0.6,
    Iyy: 0.6,
    Iuu: 0.8,
    Ivv: 0.2,
    Ixy: 0.2,
    rxx: 0.73,
    ryy: 0.73,
    ruu: 0.85,
    rvv: 0.42,
    Zxx: 0.4,
    Zyy: 0.4,
    r1: 4,
    r2: 2.5
  },
  {
    designation: "ISA 2525",
    size: "25 x 25",
    A: 25,
    B: 25,
    t: 3,
    a: 1.41,
    w: 1.1,
    Cxx: 0.71,
    Cyy: 0.71,
    exx: 1.79,
    eyy: 1.79,
    Ixx: 0.8,
    Iyy: 0.8,
    Iuu: 1.2,
    Ivv: 0.4,
    Ixy: 0.4,
    rxx: 0.73,
    ryy: 0.73,
    ruu: 0.93,
    rvv: 0.47,
    Zxx: 0.4,
    Zyy: 0.4,
    r1: 4.5,
    r2: 3
  }
];

/**
 * Get all available equal angle designations
 */
export const getRolledSteelEqualAngleDesignations = (): string[] => {
  return rolledSteelEqualAngleSections.map(section => section.designation);
};

/**
 * Get equal angle data by designation
 */
export const getRolledSteelEqualAngleByDesignation = (designation: string): RolledSteelEqualAngleData | null => {
  return rolledSteelEqualAngleSections.find(section => section.designation === designation) || null;
};
